import type { GlassVariant, Shape, SizeSurcharge } from "../types/product";
import type { ProcessingRequest } from "../types/config";
import { gehardGelaagdToeslagPct, type GrindFamily } from "./grinding";

/**
 * Modeltoeslag op het GLAS, in procenten: het catalogus-percentage van Timmers (40% één schuine
 * zijde, 80% toog, 120% cirkel). Eén plek voor de hele stack — de configurator toont ditzelfde
 * percentage op de vorm-chips, zodat scherm en prijs niet uiteen kunnen lopen.
 *
 * Bij gehard-gelaagd staat er een eigen paneeltoeslag-tabel naast (pag. 20); daar houden we de
 * hoogste van de twee aan, want het gaat om dezelfde toeslag uit twee bronnen — niet om twee
 * losse toeslagen.
 *
 * Het slijpen van een model zit hier NIET in. De volgorde is: glas → vormtoeslag → bewerking.
 * Dat een cirkel duurder is om te slijpen zit in het slijptarief zelf (ppShapeFactor), niet nog
 * eens als opslag op de glasprijs. (Vastgelegd door Thijs, 28-07-2026.)
 */
export function glasModelPct(opts: {
  shape?: Shape;
  family: GrindFamily;
  /** Zit er een ronde hoek op? Telt bij gehard-gelaagd mee als paneeltoeslag. */
  heeftRondeHoek?: boolean;
}): number {
  const { shape, family, heeftRondeHoek } = opts;
  if (!shape || shape.id === "rechthoek") return 0;
  const catalogus = (shape.surchargePct ?? 0) * 100;
  return family === "gehard-gelaagd"
    ? Math.max(catalogus, gehardGelaagdToeslagPct({ shapeId: shape.id, heeftRondeHoek }))
    : catalogus;
}

/**
 * Vanaf deze straal is een afgeronde hoek geen BEWERKING meer maar een MODEL (prijslijst pag. 5).
 * De hoek-staffel van Timmers loopt dan ook precies tot 250 mm; daarboven staat er geen stukprijs.
 */
export const RONDE_HOEK_MODEL_MM = 250;

/**
 * Modeltoeslag op het glas voor afrondingen > 250 mm — een PANEELtoeslag (percentage over de
 * glasprijs), geen stukprijs. Het percentage hangt af van HOEVEEL hoeken zo groot afgerond zijn;
 * de Timmers-modellenlijst noemt ze los:
 *
 *   "Model 1 ronde hoek >R250"      → 80%
 *   "Model 2x ronde hoek >R250"     → 80%
 *   "Model 4x Ronde hoek >250 mm"   → 120%
 *
 * Drie hoeken staat er niet bij; die volgt het 4x-model (120%) — dichter bij de werkelijkheid
 * dan 't goedkopere 2x-model, en niet iets om stilzwijgend te laag te zetten.
 *
 * Stapelt WEL op een vormtoeslag (een toog met een grote afronding zijn twee losse modelregels).
 * Dat is bewust anders dan gehard-gelaagd, waar de prijslijst expliciet één toeslag per paneel
 * noemt en we dus de hoogste nemen (zie gehardGelaagdToeslagPct).
 */
export const RONDE_HOEK_MODEL_PCT_1_2 = 80;
export const RONDE_HOEK_MODEL_PCT_3_4 = 120;

/** Modeltoeslag-% voor `aantal` afrondingen > 250 mm. 0 hoeken = geen toeslag. */
export function rondeHoekModelPct(aantal: number): number {
  if (!(aantal > 0)) return 0;
  return aantal <= 2 ? RONDE_HOEK_MODEL_PCT_1_2 : RONDE_HOEK_MODEL_PCT_3_4;
}

/** Of deze bewerking een afronding is die als model (paneeltoeslag) telt i.p.v. per stuk. */
export function isModelRoundedCorner(p: ProcessingRequest): boolean {
  return p.kind === "hoek" && (p.hoekType ?? "rond") === "rond" && p.sizeMm > RONDE_HOEK_MODEL_MM;
}

/** Aantal afrondingen > 250 mm in een set bewerkingen (telt de aantallen per regel op). */
export function modelRoundedCornerCount(processings?: ProcessingRequest[]): number {
  return (processings ?? []).filter(isModelRoundedCorner).reduce((n, p) => n + p.count, 0);
}

/** The big-pane surcharge percentage for an area: largest tier at or below it. */
export function pickSizeSurchargePct(
  tiers: SizeSurcharge[] | undefined,
  areaM2: number,
): number {
  if (!tiers?.length) return 0;
  const matching = tiers
    .filter((t) => areaM2 >= t.fromM2)
    .sort((a, b) => b.fromM2 - a.fromM2)[0];
  return matching?.pct ?? 0;
}

/** Big-pane surcharge ("toeslag groot glas") on the glass price (raw). */
export function sizeSurcharge(
  glassRaw: number,
  areaM2: number,
  tiers?: SizeSurcharge[],
): { pct: number; amount: number } {
  const pct = pickSizeSurchargePct(tiers, areaM2);
  return { pct, amount: glassRaw * (pct / 100) };
}

/** Glass weight in kg: raw area (2 decimals) × thickness × 2.5 (mirrors Timmers). */
export function glassWeightKg(areaM2: number, thicknessMm: number): number {
  return (Math.round(areaM2 * 100) / 100) * thicknessMm * 2.5;
}

/** Energietoeslag: €/kg over the glass weight (raw, unrounded). */
export function energySurcharge(
  areaM2: number,
  variant: GlassVariant,
): { weightKg: number; amount: number } {
  const weightKg = glassWeightKg(areaM2, variant.thicknessMm);
  return { weightKg, amount: weightKg * (variant.energyPerKg ?? 0) };
}
